<template>
  <v-container>
    <v-row align="center" class="mb-4">
      <v-col cols="auto">
        <v-btn elevation="2" fab small @click="$router.go(-1)">
          <v-icon color="primary">mdi-arrow-left-bold</v-icon>
        </v-btn>
      </v-col>
      <v-col>
        <h1 class="text-h4 mb-0">Upozornenia</h1>
      </v-col>
    </v-row>

    <v-row class="mb-4" align="center">
      <v-col cols="auto">
        <v-switch v-model="unreadOnly" label="Len neprečítané" hide-details class="mt-0"></v-switch>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-btn text color="primary" @click="handleMarkAllAsRead">
          Označiť všetky ako prečítané
        </v-btn>
        <v-btn icon to="/settings/notifications">
          <v-icon>mdi-cog</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card :loading="isLoading">
          <v-list two-line>
            <template v-if="notifications.length > 0">
              <div v-for="(notification, index) in notifications" :key="notification.id">
                <v-hover v-slot="{ hover }">
                  <v-list-item @click="handleNotificationClick(notification)"
                    :class="{ 'grey lighten-4': !notification.read_at, 'elevation-1': hover }"
                    style="cursor: pointer; transition: background-color 0.2s;">
                    <v-list-item-avatar>
                      <v-icon :color="getIconColor(notification.notification_type)">
                        {{ getIcon(notification.notification_type) }}
                      </v-icon>
                    </v-list-item-avatar>

                    <v-list-item-content>
                      <v-list-item-title :class="{ 'font-weight-bold': !notification.read_at }">
                        {{ notification.subject }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span class="text--primary">{{ notification.author_name }}</span>
                        &mdash; {{ formatDate(notification.sent_at) }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-if="notification.category_name">
                        {{ notification.category_name }} <span v-if="notification.poll_name"> > {{
                          notification.poll_name }}</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-btn icon @click.stop="markAsRead(notification.id)" v-if="!notification.read_at"
                        title="Označiť ako prečítané">
                        <v-icon color="grey lighten-1">mdi-checkbox-blank-circle-outline</v-icon>
                      </v-btn>
                      <v-icon v-else color="green">mdi-check</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-hover>
                <v-divider v-if="index < notifications.length - 1"></v-divider>
              </div>
            </template>
            <v-list-item v-else-if="!isLoading">
              <v-list-item-content>
                <v-list-item-title class="text-center grey--text">
                  Žiadne upozornenia
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <div class="text-center py-4" v-if="totalPages > 1">
            <v-pagination v-model="page" :length="totalPages" @input="loadNotifications"></v-pagination>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'NotificationCenter',
  data() {
    return {
      unreadOnly: false,
      page: 1,
      totalPages: 1 // Ideally this comes from API response pagination
    };
  },
  computed: {
    ...mapGetters('userNotifications', {
      notificationsData: 'getNotifications',
      isLoading: 'isLoading'
    }),
    notifications() {
      return this.notificationsData.results || [];
    }
  },
  watch: {
    unreadOnly() {
      this.page = 1;
      this.loadNotifications();
    },
    'notificationsData.count'(newCount) {
      // Calculate total pages assuming page size of 20 (or whatever API uses)
      // Since we don't have page_size in response usually, we might guess or check response structure better if implemented fully
      // Assuming Standard Pagination
      this.totalPages = Math.ceil(newCount / 20) || 1;
    }
  },
  created() {
    this.loadNotifications();
  },
  methods: {
    ...mapActions('userNotifications', ['fetchNotifications', 'markAsRead', 'markAllAsRead']),

    loadNotifications() {
      this.fetchNotifications({
        page: this.page,
        unread: this.unreadOnly ? 'true' : null
      });
    },

    handleMarkAllAsRead() {
      this.markAllAsRead().then(() => {
        this.loadNotifications();
      });
    },

    handleNotificationClick(notification) {
      if (!notification.read_at) {
        this.markAsRead(notification.id);
      }
      if (notification.content_url) {
        // Fix for backend sending absolute URLs with 127.0.0.1 vs localhost usage
        let urlToOpen = notification.content_url;

        try {
          // Check if it's a full URL
          if (urlToOpen.startsWith('http')) {
            const urlObj = new URL(urlToOpen);
            // If it is our own backend serving it (assuming relative path consistency), just take the path
            // We assume anything starting slightly weird (port 80 vs 8080) is intended to be internal if it shares the general app structure
            // But safer: just take pathname + search + hash
            this.$router.push(urlObj.pathname + urlObj.search + urlObj.hash);
          } else {
            // Already relative
            this.$router.push(urlToOpen);
          }
        } catch (e) {
          // Fallback for non-URL strings (likely relative paths)
          this.$router.push(urlToOpen);
        }
      }
    },

    formatDate(date) {
      const msPerMinute = 60 * 1000;
      const msPerHour = msPerMinute * 60;
      const msPerDay = msPerHour * 24;
      const msPerMonth = msPerDay * 30;
      const msPerYear = msPerDay * 365;

      const elapsed = new Date(date) - new Date();
      const absElapsed = Math.abs(elapsed);

      const rtf = new Intl.RelativeTimeFormat(navigator.language, { numeric: 'auto' });

      if (absElapsed < msPerMinute) {
        return rtf.format(Math.round(elapsed / 1000), 'second');
      } else if (absElapsed < msPerHour) {
        return rtf.format(Math.round(elapsed / msPerMinute), 'minute');
      } else if (absElapsed < msPerDay) {
        return rtf.format(Math.round(elapsed / msPerHour), 'hour');
      } else if (absElapsed < msPerMonth) {
        return rtf.format(Math.round(elapsed / msPerDay), 'day');
      } else if (absElapsed < msPerYear) {
        return rtf.format(Math.round(elapsed / msPerMonth), 'month');
      } else {
        return rtf.format(Math.round(elapsed / msPerYear), 'year');
      }
    },

    getIcon(type) {
      const map = {
        'NEW_QUESTION': 'mdi-help-circle',
        'DISCUSSION_REPLY': 'mdi-forum',
        'SYSTEM': 'mdi-information',
        'ADMIN_IMPORTANT': 'mdi-alert'
      };
      return map[type] || 'mdi-bell';
    },

    getIconColor(type) {
      const map = {
        'NEW_QUESTION': 'blue',
        'DISCUSSION_REPLY': 'green',
        'SYSTEM': 'grey',
        'ADMIN_IMPORTANT': 'red'
      };
      return map[type] || 'grey';
    }
  }
};
</script>
